/**
 * ============================================================================
 * MODAL MANAGER
 * ============================================================================
 *
 * Manages modal dialogs and overlays.
 *
 * ============================================================================
 */



class ModalManager {
  constructor() {
    this.activeModals = new Set();
    this.scrollPosition = 0;

    this.init();
  }

  /**
   * Initialize modal manager
   */
  init() {
    this.bindEvents();
    console.log('🔲 Modal Manager initialized');
  }

  /**
   * Bind events
   */
  bindEvents() {
    // Modal triggers
    document.addEventListener('click', (e) => {
      // Open modal
      if (e.target.matches('[data-modal-open]')) {
        e.preventDefault();
        const modalId = data(e.target, 'modal-open');
        this.openModal(modalId);
      }

      // Close modal
      if (e.target.matches('[data-modal-close]')) {
        e.preventDefault();
        const modalId = data(e.target, 'modal-close');
        if (modalId) {
          this.closeModal(modalId);
        } else {
          this.closeTopModal();
        }
      }

      // Close on backdrop click
      if (e.target.matches('.modal-backdrop')) {
        const modal = e.target.nextElementSibling;
        if (modal && hasClass(modal, 'modal')) {
          const modalId = modal.id;
          if (!data(modal, 'backdrop-static')) {
            this.closeModal(modalId);
          }
        }
      }
    });

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeModals.size > 0) {
        this.closeTopModal();
      }
    });
  }

  /**
   * Open modal
   * @param {string} modalId - Modal ID
   * @param {Object} options - Modal options
   */
  openModal(modalId, options = {}) {
    const modal = $(`#${modalId}`);
    if (!modal) {
      console.warn(`Modal with ID "${modalId}" not found`);
      return;
    }

    // Store scroll position if this is the first modal
    if (this.activeModals.size === 0) {
      this.scrollPosition = window.pageYOffset;
      this.preventBodyScroll();
    }

    // Create backdrop if it doesn't exist
    let backdrop = modal.previousElementSibling;
    if (!backdrop || !hasClass(backdrop, 'modal-backdrop')) {
      backdrop = this.createBackdrop();
      modal.parentNode.insertBefore(backdrop, modal);
    }

    // Add to active modals
    this.activeModals.add(modalId);

    // Show backdrop
    addClass(backdrop, 'show');

    // Show modal
    addClass(modal, 'show');
    modal.setAttribute('aria-hidden', 'false');

    // Focus management
    this.trapFocus(modal);

    // Emit event
    document.dispatchEvent(new CustomEvent('modal:open', {
      detail: { modalId, modal }
    }));

    console.log(`📖 Modal "${modalId}" opened`);
  }

  /**
   * Close modal
   * @param {string} modalId - Modal ID
   */
  closeModal(modalId) {
    const modal = $(`#${modalId}`);
    if (!modal || !this.activeModals.has(modalId)) {
      return;
    }

    // Remove from active modals
    this.activeModals.delete(modalId);

    // Hide modal
    removeClass(modal, 'show');
    modal.setAttribute('aria-hidden', 'true');

    // Hide backdrop
    const backdrop = modal.previousElementSibling;
    if (backdrop && hasClass(backdrop, 'modal-backdrop')) {
      removeClass(backdrop, 'show');

      // Remove backdrop after transition
      setTimeout(() => {
        if (backdrop.parentNode) {
          backdrop.parentNode.removeChild(backdrop);
        }
      }, 300);
    }

    // Restore body scroll if no more modals
    if (this.activeModals.size === 0) {
      this.restoreBodyScroll();
    }

    // Restore focus
    this.restoreFocus();

    // Emit event
    document.dispatchEvent(new CustomEvent('modal:close', {
      detail: { modalId, modal }
    }));

    console.log(`📕 Modal "${modalId}" closed`);
  }

  /**
   * Close the topmost modal
   */
  closeTopModal() {
    if (this.activeModals.size > 0) {
      const modalIds = Array.from(this.activeModals);
      const topModalId = modalIds[modalIds.length - 1];
      this.closeModal(topModalId);
    }
  }

  /**
   * Close all modals
   */
  closeAllModals() {
    const modalIds = Array.from(this.activeModals);
    modalIds.forEach(modalId => this.closeModal(modalId));
  }

  /**
   * Create backdrop element
   * @returns {Element} Backdrop element
   */
  createBackdrop() {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    return backdrop;
  }

  /**
   * Prevent body scroll
   */
  preventBodyScroll() {
    addClass(document.body, 'modal-open');
    document.body.style.top = `-${this.scrollPosition}px`;
  }

  /**
   * Restore body scroll
   */
  restoreBodyScroll() {
    removeClass(document.body, 'modal-open');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollPosition);
  }

  /**
   * Trap focus within modal
   * @param {Element} modal - Modal element
   */
  trapFocus(modal) {
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element
    firstElement.focus();

    // Handle tab navigation
    const handleTabKey = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);

    // Store handler for cleanup
    modal._focusTrapHandler = handleTabKey;
  }

  /**
   * Restore focus to trigger element
   */
  restoreFocus() {
    // Implementation depends on how you want to handle focus restoration
    // You might want to store the trigger element when opening the modal
  }

  /**
   * Check if modal is open
   * @param {string} modalId - Modal ID
   * @returns {boolean} True if modal is open
   */
  isModalOpen(modalId) {
    return this.activeModals.has(modalId);
  }

  /**
   * Get active modals
   * @returns {Set} Set of active modal IDs
   */
  getActiveModals() {
    return new Set(this.activeModals);
  }
}

// Make ModalManager available globally
window.ModalManager = ModalManager;
